<template>
  <div
    :id="pageId"
    class="page-content"
    :class="pageId"
  >
    <div class="steps-container">
      <el-steps
        :active="activeStep"
        finish-status="success"
        align-center
      >
        <el-step
          v-for="(item, index) in steps"
          :key="index"
          :class="{ clickable: isAllowToMove(item.step) }"
          :title="`Step ${index + 1}`"
          :description="item.description"
          @click="onClickStep(item.step)"
        />
      </el-steps>
    </div>

    <create-partner
      v-if="activeStep === PartnerRegistrationSteps.CreateUpdatePartnerInfo"
      @goto-next-step="nextStep"
    />

    <communication
      v-if="activeStep === PartnerRegistrationSteps.Communication"
      @goto-next-step="nextStep"
    />

    <enrollment-criteria
      v-if="activeStep === PartnerRegistrationSteps.EnrollmentCriteria"
      @goto-next-step="nextStep"
    />

    <subsidy-info
      v-if="activeStep === PartnerRegistrationSteps.SubsidyInfo"
      @goto-next-step="nextStep"
    />

    <service-times
      v-if="activeStep === PartnerRegistrationSteps.ServiceTimes"
      @goto-next-step="nextStep"
    />

    <taxi-vendors
      v-if="activeStep === PartnerRegistrationSteps.TaxiVendors"
      @goto-next-step="nextStep"
    />

    <payment
      v-if="activeStep === PartnerRegistrationSteps.Payment"
      @goto-next-step="nextStep"
    />

    <div class="form-submit-button-group mb-2">
      <p-button
        v-if="isBackBtnVisible"
        id="btnBack"
        type="warning"
        round
        class="mr-2"
        @click="backStep"
      >
        {{ `Back To Step ${activeStep}` }}
      </p-button>

      <p-button
        v-if="isNextBtnVisible"
        id="btnNext"
        type="purple"
        round
        @click="nextStep"
      >
        {{ nextBtnText }}
      </p-button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, watch, onMounted, onBeforeUnmount, defineAsyncComponent } from 'vue';
import { useRouter, useRoute } from 'vue-router';
import { ElLoading } from 'element-plus';

import util from '@/Utils/util';
import { useCustomer } from '@/use/customer';
import { useSwalDialog } from '@/use/swalDialog';
import { usePartner } from '@/use/partner';
import { PartnerRegistrationSteps } from '@/models/partner';
import { fetchPartner } from '@/services/partners/fetchPartner';
import Communication from '@/components/Main/Operator/Partners/PartnerRegistration/Communication.vue';

const CreatePartner = defineAsyncComponent(() => import('./CreatePartner.vue') as any);
const SubsidyInfo = defineAsyncComponent(() => import('./SubsidyInfo.vue') as any);
const Payment = defineAsyncComponent(() => import('./Payment.vue') as any);
const ServiceTimes = defineAsyncComponent(() => import('./ServiceTimes.vue') as any);
const TaxiVendors = defineAsyncComponent(() => import('./TaxiVendors.vue') as any);
const EnrollmentCriteria = defineAsyncComponent(() => import('./EnrollmentCriteria.vue') as any);

const pageId = 'partner-registration-page';
const steps: {
  id: number;
  description: string;
  step: PartnerRegistrationSteps;
}[] = [
  { id: 1, description: 'Partner Information', step: PartnerRegistrationSteps.CreateUpdatePartnerInfo },
  { id: 2, description: 'Settings and Communication', step: PartnerRegistrationSteps.Communication },
  { id: 3, description: 'Enrollment Criteria', step: PartnerRegistrationSteps.EnrollmentCriteria },
  { id: 4, description: 'Subsidy Info', step: PartnerRegistrationSteps.SubsidyInfo },
  { id: 5, description: 'Service Times', step: PartnerRegistrationSteps.ServiceTimes },
  { id: 6, description: 'Taxi Vendors', step: PartnerRegistrationSteps.TaxiVendors },
  { id: 7, description: 'Billing Information', step: PartnerRegistrationSteps.Payment },
];

const router = useRouter();
const route = useRoute();

const { customerPhoneNumber } = useCustomer();
const { showSuccessPopupDialog, showErrorPopupDialog } = useSwalDialog();
const { newPartnerInfo, setNewPartnerInfo } = usePartner();

const activeStep = ref(PartnerRegistrationSteps.CreateUpdatePartnerInfo);

const codeName = ref('');

const nextBtnText = computed(() => {
  if (activeStep.value !== PartnerRegistrationSteps.Payment) {
    return 'Go to Next Step';
  }

  return codeName.value ? 'Complete' : 'Complete Registration';
});

const isNextBtnVisible = computed(() => {
  if (activeStep.value !== PartnerRegistrationSteps.CreateUpdatePartnerInfo) {
    return true;
  }

  if (newPartnerInfo.value?.id) {
    return true;
  }

  return false;
});

const isBackBtnVisible = computed(() => activeStep.value !== PartnerRegistrationSteps.CreateUpdatePartnerInfo);

const isAllowToMove = (step: PartnerRegistrationSteps) => {
  // Should not go forward to any steps if partner is not created
  if (!newPartnerInfo.value?.id) {
    return false;
  }

  return true;
};

const onClickStep = (step: PartnerRegistrationSteps) => {
  const isMoveable = isAllowToMove(step);

  if (!isMoveable) {
    return;
  }

  activeStep.value = step;
};

const backStep = () => {
  activeStep.value--;
};

const nextStep = async () => {
  if (activeStep.value === PartnerRegistrationSteps.Payment) {
    await showSuccessPopupDialog({
      title: 'Completed!',
      text: 'You will be redirected to the Partners page.',
    });

    setNewPartnerInfo(null);

    router.push({
      name: 'Partners',
      query: {
        phoneNumber: customerPhoneNumber.value,
      },
    });

    return;
  }

  activeStep.value++;
};

const getPartnerInfo = async () => {
  const loading = ElLoading.service({
    lock: true,
    text: 'Loading Partner Info...',
    background: 'rgba(0, 0, 0, 0.7)',
    customClass: 'custom-full-spinner-class',
  });
  try {
    const response = await fetchPartner({
      codeName: codeName.value,
    });

    loading.close();

    if (!response) {
      await showErrorPopupDialog({
        text: 'There is an invalid partner code',
      });

      router.push({
        name: 'Partners',
        query: {
          phoneNumber: customerPhoneNumber.value,
        },
      });
    }

    setNewPartnerInfo(response);
  } catch (error) {
    const errorText = util.getErrorResponse(error, false);
    await showErrorPopupDialog({
      text: errorText,
    });

    router.push({
      name: 'Partners',
      query: {
        phoneNumber: customerPhoneNumber.value,
      },
    });
  }
};

watch(activeStep, util.moveToTop, {
  immediate: true,
});

onMounted(() => {
  codeName.value = (route.params.codeName as string) || '';

  if (codeName.value) {
    getPartnerInfo();
  }
});

onBeforeUnmount(() => {
  setNewPartnerInfo(null);
});
</script>

<style lang="scss">
.partner-registration-page {
  h3.title {
    text-align: center;
    margin-top: 20px;
    margin-bottom: 10px;
    color: #54a065;
  }

  .el-step {
    pointer-events: none;

    &.clickable {
      .el-step__head {
        .el-step__icon {
          pointer-events: all;
          cursor: pointer;
        }
      }
    }
  }
}
</style>
